import {
  Body,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Heading,
  Icon,
  Spacer,
  useTheme,
} from '@hover/blueprint';
import type { IconProp } from '@hover/icons';
import { iconDamage, iconResidential, iconWall } from '@hover/icons';
import React from 'react';
import { useHistory } from 'react-router';

import { BackButton } from 'src/modules/onboarding/components/BackButton';
import { useEffectOnMount, useLocation, useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { Route as Routes } from 'src/shared/route';
import { pageViewed } from 'src/shared/telemetry';

type UserTypeOptionType = {
  type: string;
  header: string;
  subheader: string;
  icon: IconProp;
};

const userTypeOptions: UserTypeOptionType[] = [
  {
    type: 'pro',
    header: "I'm a construction pro",
    subheader: "I'll use Hover for my work or business",
    icon: iconWall,
  },

  {
    type: 'insurance',
    header: "I'm an insurance pro",
    subheader: "I'll use Hover for adjustments and claims",
    icon: iconDamage,
  },
  {
    type: 'homeowner',
    header: 'Homeowner',
    subheader: "I'll use Hover for projects on my property",
    icon: iconResidential,
  },
];

const UserTypeOption = ({
  type,
  icon,
  header,
  subheader,
}: UserTypeOptionType) => {
  const { search } = useLocation();

  return (
    <Button
      as="a"
      borderColor="neutral.300"
      borderRadius="8px"
      borderWidth="1px"
      height={{ base: '92px', tablet: '72px' }}
      href={`${Routes.SIGN_UP_ONBOARDING.path}/${type}${search}`}
      marginBottom={{ base: 300, tablet: 400 }}
      shape="box"
      sx={{
        '& .button-label': {
          justifySelf: 'flex-start',
          width: '100%',
        },
        '&:hover': {
          borderColor: 'black',
        },
      }}
      variant="secondary"
      width={{ base: 1, tablet: '432px' }}
    >
      <Box flexDirection="row" width={1}>
        <Icon alignSelf="center" icon={icon} size="medium" />
        <Box flexDirection="column" marginLeft="12px">
          <Heading size={200}>{header}</Heading>
          <Body
            as={Box}
            color="neutralText"
            opacity={0.64}
            size="small"
            whiteSpace="wrap"
          >
            {subheader}
          </Body>
        </Box>
      </Box>
    </Button>
  );
};

export const Start = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const { breakpoints } = useTheme();
  const history = useHistory();
  const goBack = () => history.goBack();

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Signup_fork',
      feature: 'onboarding_reskin',
    });
  });

  return (
    <>
      <Grid
        flex={1}
        height={{ base: '100dvh', tablet: 1 }}
        width={{ base: 'auto', tablet: 1 }}
      >
        <Flex
          flexDirection="column"
          height={1}
          justifyContent="space-between"
          paddingX={500}
        >
          <Box justifyContent="center">
            <HStack
              alignItems="start"
              flexWrap="wrap"
              justifyContent={{ base: 'start', tablet: 'space-between' }}
              marginTop={300}
              maxWidth={{ base: 'auto', tablet: breakpoints.width500 }}
              spacing={0}
              width={1}
            >
              <Heading
                paddingBottom={{ base: 500 }}
                size={{ base: 600, tablet: 800 }}
                whiteSpace="pre"
              >
                {t('onboarding.whichOptionBestDescribesYou')}
              </Heading>
              <Box flexDirection="column" width={{ base: 1, tablet: 'auto' }}>
                {userTypeOptions.map(option => (
                  <UserTypeOption key={option.type} {...option} />
                ))}
              </Box>
            </HStack>
          </Box>
          <Spacer flex={1} />
        </Flex>
      </Grid>
      <Box
        alignItems="center"
        display={{ base: 'none', tablet: 'flex' }}
        flexDirection="column"
        width={1}
      >
        <Box
          justifyContent="space-between"
          maxWidth={breakpoints.width500}
          paddingBottom={300}
          width={1}
        >
          <BackButton onClick={goBack} />
        </Box>
      </Box>
    </>
  );
};
